import { array, option, record, string } from 'fp-ts';
import { flow } from 'fp-ts/function';
import { UnicodeEntity } from '@mews/enums';
import { Duration, DurationService, useLocalizeText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';

const offsetUnitTextKeyMapShort = {
    days: TextKey.PaymentPolicyTimingDaysShort,
    hours: TextKey.PaymentPolicyTimingHoursShort,
    minutes: TextKey.PaymentPolicyTimingMinutesShort,
    seconds: TextKey.PaymentPolicyTimingSecondsShort,
};

const offsetUnitTextKeyMapLong = {
    days: TextKey.PaymentPolicyTimingDays,
    hours: TextKey.PaymentPolicyTimingHours,
    minutes: TextKey.PaymentPolicyTimingMinutes,
    seconds: TextKey.PaymentPolicyTimingSeconds,
};

type TimeUnit = keyof typeof offsetUnitTextKeyMapShort;

interface UseLocalizeOffsetValuesParams {
    format?: 'short' | 'long';
    timeUnits?: Array<keyof typeof offsetUnitTextKeyMapShort>;
}

const defaultParams = {
    format: 'short',
    timeUnits: ['days', 'hours', 'minutes'],
} as const;

export const useLocalizeOffsetValues = (params?: UseLocalizeOffsetValuesParams) => {
    const localizeText = useLocalizeText();

    const format = params?.format ?? defaultParams.format;
    const timeUnits = params?.timeUnits ?? defaultParams.timeUnits;
    const offsetUnitTextKeyMap = format === 'short' ? offsetUnitTextKeyMapShort : offsetUnitTextKeyMapLong;

    return flow(
        DurationService.absolute,
        (duration) => DurationService.toObject(duration, { partial: false }),
        (duration) =>
            timeUnits.reduce((acc, unit) => ({ ...acc, [unit]: duration[unit] }), {} as Record<TimeUnit, number>),
        record.toEntries,
        array.filterMap(([unit, unitValue]) =>
            unitValue !== 0
                ? option.some(localizeText({ textKey: offsetUnitTextKeyMap[unit], substitutes: { count: unitValue } }))
                : option.none
        ),
        array.intersperse<string>(UnicodeEntity.NoBreakSpace),
        (values) => values.join(string.empty)
    );
};

export const LocalizedOffsetValues = ({ duration }: { duration: Duration }) => {
    const localizeOffsetValues = useLocalizeOffsetValues();
    return <>{localizeOffsetValues(duration)}</>;
};
